import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AppLayout from '../components/layout/AppLayout';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const styles = {
  container: { maxWidth: '900px', margin: '0 auto', padding: '40px 20px' },
  title: { fontSize: '32px', fontWeight: '800', color: '#111827', textAlign: 'center', marginBottom: '8px' },
  sub: { fontSize: '16px', color: '#6b7280', textAlign: 'center', marginBottom: '40px' },
  toggle: { display: 'flex', justifyContent: 'center', gap: '4px', marginBottom: '40px', backgroundColor: '#f3f4f6', borderRadius: '10px', padding: '4px', width: 'fit-content', margin: '0 auto 40px' },
  toggleBtn: { padding: '8px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', backgroundColor: 'transparent', color: '#6b7280', fontFamily: 'inherit' },
  toggleBtnActive: { backgroundColor: '#fff', color: '#111827', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '40px' },
  card: { backgroundColor: '#fff', borderRadius: '16px', padding: '32px', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  cardPro: { backgroundColor: '#fff', borderRadius: '16px', padding: '32px', border: '2px solid #0052CC', boxShadow: '0 4px 20px rgba(0,82,204,0.15)', position: 'relative' },
  popularBadge: { position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', backgroundColor: '#0052CC', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px' },
  planName: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' },
  price: { fontSize: '40px', fontWeight: '800', color: '#0052CC', marginBottom: '4px' },
  priceSub: { fontSize: '13px', color: '#6b7280', marginBottom: '24px' },
  features: { listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '10px' },
  feature: { fontSize: '13px', color: '#374151', display: 'flex', gap: '8px', alignItems: 'flex-start' },
  featureIcon: { color: '#0052CC', fontWeight: '700', flexShrink: 0 },
  btn: { width: '100%', padding: '12px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '700', fontFamily: 'inherit' },
  btnPrimary: { backgroundColor: '#0052CC', color: '#fff' },
  btnSecondary: { backgroundColor: '#f3f4f6', color: '#374151' },
  btnEnterprise: { backgroundColor: '#111827', color: '#fff' },
  saving: { fontSize: '11px', color: '#16a34a', fontWeight: '700', marginLeft: '8px' },
};

const FEATURES = {
  standard: [
    '3 lab simulations/day',
    '5 practice sessions/day',
    '2 agents per scenario',
    'Max 6 turns per simulation',
    'Voice + STT mode',
    'Acoustic perturbation (categorical + parametric)',
    'Basic evaluation (overall score)',
    'Claude (Anthropic) only',
    '7-day simulation history',
  ],
  professional: [
    'Unlimited lab simulations',
    'Unlimited practice sessions',
    'Up to 6 agents per scenario',
    'Unlimited turns per simulation',
    'Voice + STT mode',
    'Acoustic perturbation (categorical + parametric)',
    'Full 3-layer evaluation engine',
    'WER analytics + disorder analysis',
    'All LLM providers (Claude, GPT-4o, Gemini)',
    'Corpus export (JSON + CSV)',
    'Full simulation history',
    'REST API access',
    'Email support (48hr)',
  ],
  enterprise: [
    'Everything in Professional',
    'Team accounts',
    'SSO / institutional auth',
    'Batch simulation runs via API',
    'Custom disorder profiles',
    'Custom data retention',
    'SLA guarantee',
    'Dedicated account manager',
    'Annual invoicing',
    'White-label option',
  ],
};

export default function Upgrade() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [billing, setBilling] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const currentPlan = userProfile?.plan || 'standard';

  async function handleUpgrade() {
    if (!currentUser) { navigate('/login'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_id: currentUser.uid,
          email: currentUser.email,
          billing,
        }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Failed to create checkout session');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AppLayout>
      <div style={styles.container}>
        <div style={styles.title}>Choose your plan</div>
        <div style={styles.sub}>Start free. Upgrade when you need more.</div>

        {/* Billing toggle */}
        <div style={styles.toggle}>
          {[
            { id: 'monthly', label: 'Monthly' },
            { id: 'annual', label: <>Annual <span style={styles.saving}>Save 17%</span></> },
          ].map(b => (
            <button
              key={b.id}
              style={{ ...styles.toggleBtn, ...(billing === b.id ? styles.toggleBtnActive : {}) }}
              onClick={() => setBilling(b.id)}
            >
              {b.label}
            </button>
          ))}
        </div>

        {error && (
          <div style={{ color: '#dc2626', textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {/* Plan cards */}
        <div style={styles.grid}>
          {/* Standard */}
          <div style={styles.card}>
            <div style={styles.planName}>Standard</div>
            <div style={styles.price}>$0</div>
            <div style={styles.priceSub}>Free forever</div>
            <ul style={styles.features}>
              {FEATURES.standard.map((f, i) => (
                <li key={i} style={styles.feature}>
                  <span style={styles.featureIcon}>✓</span>{f}
                </li>
              ))}
            </ul>
            <button
              style={{ ...styles.btn, ...styles.btnSecondary }}
              disabled
            >
              {currentPlan === 'standard' ? 'Current plan' : 'Downgrade'}
            </button>
          </div>

          {/* Professional */}
          <div style={styles.cardPro}>
            <div style={styles.popularBadge}>Most Popular</div>
            <div style={styles.planName}>Professional</div>
            <div style={styles.price}>
              {billing === 'annual' ? '$84' : '$100'}
            </div>
            <div style={styles.priceSub}>
              {billing === 'annual' ? 'per month, billed $1,000/year' : 'per month'}
            </div>
            <ul style={styles.features}>
              {FEATURES.professional.map((f, i) => (
                <li key={i} style={styles.feature}>
                  <span style={styles.featureIcon}>✓</span>{f}
                </li>
              ))}
            </ul>
            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={handleUpgrade}
              disabled={loading || currentPlan === 'professional' || currentPlan === 'enterprise'}
            >
              {loading ? 'Redirecting...' :
               currentPlan === 'professional' ? 'Current plan' :
               currentPlan === 'enterprise' ? 'Included in Enterprise' :
               `Upgrade to Professional`}
            </button>
          </div>

          {/* Enterprise */}
          <div style={styles.card}>
            <div style={styles.planName}>Enterprise</div>
            <div style={styles.price}>Custom</div>
            <div style={styles.priceSub}>Annual contract</div>
            <ul style={styles.features}>
              {FEATURES.enterprise.map((f, i) => (
                <li key={i} style={styles.feature}>
                  <span style={styles.featureIcon}>✓</span>{f}
                </li>
              ))}
            </ul>
            <button
              style={{ ...styles.btn, ...styles.btnEnterprise }}
              onClick={() => window.location.href = 'mailto:info@converstation.ai?subject=V2VLab Enterprise'}
            >
              Contact Sales
            </button>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
